import { ChevronDownIcon, ChevronsUpDownIcon, ChevronUpIcon } from 'lucide-react'
import type { Column } from '@tanstack/react-table'

import { Button } from '../ui/button'

/**
 * A column heading, drawn the way the fleet draws it.
 *
 * The fleet builds this inside `mapColumn`, together with the filter dropdown,
 * the `numeral` number format and the `dayjs` date format. Those three are the
 * half this console did not take — `filters/` and `export/` are out (design
 * note §6), and neither `numeral` nor `dayjs` is a dependency here, so taking
 * `mapColumn` whole would drag in two libraries to draw a chevron.
 *
 * So this is the visible half, kept to the same markup and the same classes:
 * `variant="ghost"`, `h-auto py-1 pr-1`, the same three chevrons in the same
 * three states, shift-click for a second sort key. A heading that is not
 * sortable is a plain `px-2 py-1` span, as there.
 */
export function DataTableColumnHeader<TData, TValue>({
  column,
  title,
  align = 'justify-start',
}: {
  column: Column<TData, TValue>
  title: React.ReactNode
  /** `justify-start` for text, `justify-end` for numbers, as `mapColumn` picks. */
  align?: 'justify-start' | 'justify-end' | 'justify-center'
}) {
  const sorted = column.getIsSorted()

  if (!column.getCanSort()) {
    return (
      <div className={`flex items-center ${align} gap-0.5`}>
        <span className="px-2 py-1">{title}</span>
      </div>
    )
  }

  return (
    <div className={`flex items-center ${align} gap-0.5`}>
      <Button
        variant="ghost"
        onClick={({ shiftKey }) =>
          sorted === 'desc' ? column.clearSorting() : column.toggleSorting(sorted === 'asc', shiftKey)
        }
        className="h-auto py-1 pr-1"
      >
        <span className="flex items-center gap-1">
          {title}
          {!sorted && <ChevronsUpDownIcon className="h-4 w-4" />}
          {sorted === 'asc' && <ChevronUpIcon className="h-4 w-4" />}
          {sorted === 'desc' && <ChevronDownIcon className="h-4 w-4" />}
        </span>
      </Button>
    </div>
  )
}
